import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, QueryResult } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import { Formik } from 'formik';
import { makeStyles, Typography } from '@material-ui/core';
import GET_ORGANIZATIONS_AND_ROLES_QUERY from 'graph/organization/get-organizations-and-roles-query';
import GET_ADMIN_USER_INFORMATION_QUERY from 'graph/user/get-admin-user-information';
import UPDATE_USER_ORGANIZATIONS from 'graph/user/update-user';
import useParseActionRouteParams from 'hooks/route-action/use-parse-action-route-params';
import useStore from 'store';
import { AlertVariant } from 'types/enums';
import { Organization, OrganizationRole, User } from 'types/schema-generated-types';
import { filterPublicOrgAssociationRoles } from 'utilities/organization-util';
import { getAssignedProducts } from 'utilities/product-utils';
import * as Yup from 'yup';
import UserStatusHeader from '../../components/UserStatusHeader';
import { getAdminUserManagementUsers } from '../../utils/update-cache';
import { buildUpdateUserPayload } from '../../utils/update-user-mutation-utils';
import Form from './components/Form';

const useStyles = makeStyles(theme => ({
  userStatusContainer: {
    paddingBottom: theme.spacing(3)
  }
}));

const InviteUserModalSchema = Yup.object().shape({
  organizationRoles: Yup.array()
    .min(1, 'The user must have at least one active role.')
    .required(),
  organization: Yup.string().when('organizationRoles', (organizationRoles: any, schema: any) => {
    if (!organizationRoles.length) {
      return schema.required('Required');
    }
    return schema;
  }),
  role: Yup.string().when('organizationRoles', (organizationRoles: any, schema: any) => {
    if (!organizationRoles.length) {
      return schema.required('Required');
    }
    return schema;
  })
});

const EditUserModal: React.FC = () => {
  const classes = useStyles();
  const { userId } = useParseActionRouteParams();
  const { t } = useTranslation('EditUserModal');
  const [
    {
      user: { me }
    },
    { closeModal, openAlert }
  ] = useStore();

  const { data } = useQuery(GET_ADMIN_USER_INFORMATION_QUERY, {
    fetchPolicy: 'no-cache',
    variables: { where: { id: Number(userId) } }
  });

  const [updateUser, { data: updateUserResponse, loading: updateUserLoading }] = useMutation(
    UPDATE_USER_ORGANIZATIONS,
    getAdminUserManagementUsers()
  );

  const user = data?.user as User;

  const licensedProducts = React.useMemo(() => {
    return user?.licenseUsage ? getAssignedProducts(user.licenseUsage) : [];
  }, [user]);

  const userOrganizations = useMemo(
    () =>
      user?.organizationAssociations?.reduce(
        (acc, orgAssociation) => [
          ...acc,
          ...filterPublicOrgAssociationRoles(orgAssociation.organizationRoles).map(role => ({
            organization: orgAssociation.organization,
            role
          }))
        ],
        [] as { organization: Organization; role: OrganizationRole }[]
      ) ?? [],
    [user]
  );

  const unfilteredUserOrgs = useMemo(
    () =>
      user?.organizationAssociations?.reduce(
        (acc, orgAssociation) => [
          ...acc,
          ...orgAssociation.organizationRoles.map(role => ({
            organization: orgAssociation.organization,
            role
          }))
        ],
        [] as { organization: Organization; role: OrganizationRole }[]
      ) ?? [],
    [user]
  );

  const handleFormSubmit = useCallback(
    async values => {
      const unshownRoles = unfilteredUserOrgs.filter(x => x.role.public === false);

      // hide public=false roles from ui, but if there are any, add them back
      const nextValues = {
        ...values,
        organizationRoles: [
          ...values.organizationRoles,
          ...(unshownRoles.length > 0 ? unshownRoles : [])
        ]
      };

      const updateData = buildUpdateUserPayload(nextValues);
      await updateUser({ variables: { where: { id: user.id }, data: updateData } });

      openAlert({
        variant: AlertVariant.SUCCESS,
        title: t('User Details Updated'),
        text: t('The user details have been successfully updated.')
      });
    }, // eslint-disable-next-line
    [updateUser, openAlert, t, user, me, licensedProducts]
  );

  useEffect(() => {
    if (updateUserResponse && updateUserResponse.updateUser) {
      closeModal();
    }
  }, [updateUserResponse, closeModal]);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        email: user?.username || '',
        pin: user?.pin || '',
        organizationRoles: userOrganizations,
        licensedProducts,
        organization: '',
        role: '',
        selectedProduct: ''
      }}
      onSubmit={handleFormSubmit}
      validationSchema={InviteUserModalSchema}
    >
      {formikProps => (
        <Query
          query={GET_ORGANIZATIONS_AND_ROLES_QUERY}
          variables={{
            rolesWhere: {
              organizationId: Number(formikProps.values.organization)
            }
          }}
        >
          {({ data }: QueryResult<any, Record<string, any>>): ReactElement => {
            return (
              <ModalContainer
                Header={
                  <ModalHeader>
                    <Typography variant="h2" component="h1">
                      {t('Edit User Details')}
                    </Typography>
                  </ModalHeader>
                }
                Footer={
                  <ModalFooter
                    onCancel={closeModal}
                    onSubmit={formikProps.submitForm}
                    submitDisabled={
                      !formikProps.isValid || formikProps.isSubmitting || updateUserLoading
                    }
                  />
                }
              >
                <ModalContent>
                  <div className={classes.userStatusContainer}>
                    <UserStatusHeader
                      avatarUrl={user?.avatarUrl || ''}
                      firstName={user?.firstName || ''}
                      lastName={user?.lastName || ''}
                      userStatus={user?.status}
                    />
                  </div>
                  <Form
                    {...formikProps}
                    organizations={data?.organizations}
                    organizationRoles={filterPublicOrgAssociationRoles(data?.organizationRoles)}
                  />
                </ModalContent>
              </ModalContainer>
            );
          }}
        </Query>
      )}
    </Formik>
  );
};

export default EditUserModal;
